// /src/pages/UserApprovalPage.jsx
// VERSÃO 10.0 SUPREMA - TRIAGE ROOM & 7 ESTAÇÕES SYNC

import React, { useState, useEffect } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { UserExecutionEngine } from '../api/UserExecutionEngine';
import { 
    FiShield, FiCheckCircle, FiXCircle, FiUser, 
    FiLoader, FiClock, FiActivity, FiAlertTriangle, FiCpu, FiTerminal, FiLayers
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';

export default function UserApprovalPage() {
    const [recrutas, setRecrutas] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadQuarentena = async () => {
        setLoading(true);
        try {
            const data = await UserExecutionEngine.listarQuarentena();
            setRecrutas(data);
        } catch (err) {
            console.error("💥 Falha crítica no uplink da Quarentena.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadQuarentena(); }, []);

    // ✅ PROTOCOLO DE APROVAÇÃO (PROMOÇÃO A AGENTE)
    const handleApprove = async (id) => {
        const sucesso = await UserExecutionEngine.aprovarAgente(id);
        if (sucesso) {
            toast.success('🛡️ ACESSO CONCEDIDO: AGENTE PROMOVIDO!');
            loadQuarentena();
        }
    };

    // ⚰️ PROTOCOLO DE REJEIÇÃO (EXPULSÃO DEFINITIVA)
    const handleReject = async (id) => {
        if (!window.confirm("⚠️ CONFIRMAR PROTOCOLO DE EXPULSÃO? O REGISTRO SERÁ INCINERADO.")) return;
        const sucesso = await UserExecutionEngine.expulsarAgente(id);
        if (sucesso) {
            toast.warn('🔥 REGISTRO ELIMINADO DO BANCO DE DADOS.');
            loadQuarentena();
        }
    };

    return (
        <MainLayout>
            <div className="w-full min-h-screen transition-all duration-500 pb-40" 
                 style={{ backgroundColor: 'var(--bg-main)' }}>
                
                <div className="max-w-[1600px] mx-auto p-8 md:p-16 space-y-16">
                    
                    {/* 🛰️ HEADER MONUMENTAL (HUD DE SEGURANÇA) */}
                    <header className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-10 border-b pb-16 relative overflow-hidden"
                            style={{ borderColor: 'var(--border-color)' }}>
                        
                        <div className="flex items-center gap-10 relative z-10">
                            <div className="w-24 h-24 rounded-[2.5rem] border shadow-2xl flex items-center justify-center transition-all"
                                 style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--accent-color)' }}>
                                <FiShield size={48} className="animate-pulse" />
                            </div>
                            <div>
                                <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-auto leading-none mb-4">
                                    Sala de <span className="text-accent">Triagem</span>
                                </h1>
                                <div className="flex items-center gap-6">
                                    <div className="px-5 py-2 rounded-2xl border border-auto bg-card-auto shadow-sm flex items-center gap-3">
                                        <div className="w-2 h-2 rounded-full bg-yellow-500 animate-ping" />
                                        <span className="text-[10px] font-black uppercase tracking-[0.4em] text-auto opacity-60">
                                            Quarentena de Ativos Pendentes
                                        </span>
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-accent">Status: Verificação Nível 04</span>
                                </div>
                            </div>
                        </div>

                        {/* COUNTER DE QUARENTENA */}
                        <div className="px-12 py-6 bg-card-auto border border-auto rounded-[2.5rem] shadow-2xl backdrop-blur-xl group hover:border-accent/30 transition-all">
                            <span className="block text-[9px] font-black uppercase text-auto opacity-30 tracking-[0.3em] mb-2 text-right">Aguardando Visto</span>
                            <div className="flex items-center gap-4">
                                <FiClock className="text-accent opacity-20" size={20} />
                                <span className="text-5xl font-black text-accent font-mono tracking-tighter">
                                    {recrutas.length.toString().padStart(2, '0')}
                                </span>
                            </div>
                        </div>
                    </header>

                    {/* 🚀 GRADE DE PROCESSAMENTO */}
                    {loading ? (
                        <div className="py-48 text-center flex flex-col items-center justify-center">
                            <div className="relative">
                                <FiCpu className="animate-spin text-accent mb-8 opacity-20" size={80} />
                                <FiLoader className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin-reverse text-accent" size={40} />
                            </div>
                            <p className="text-[11px] font-black uppercase tracking-[0.6em] text-auto opacity-30">Varrendo Perímetro de Segurança...</p>
                        </div>
                    ) : recrutas.length === 0 ? (
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                            className="p-40 rounded-[4rem] border-2 border-dashed text-center transition-all bg-card-auto"
                            style={{ borderColor: 'var(--border-color)', opacity: 0.8 }}
                        >
                            <FiCheckCircle className="mx-auto mb-10 text-accent opacity-20" size={100} />
                            <h2 className="text-4xl font-black text-auto uppercase tracking-tighter mb-4">Perímetro Limpo</h2>
                            <p className="text-[10px] font-black uppercase tracking-[0.5em] text-accent">Todos os agentes foram processados e autorizados.</p>
                        </motion.div>
                    ) : (
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-10">
                            <AnimatePresence mode="popLayout">
                                {recrutas.map((recruta) => (
                                    <motion.div 
                                        layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9 }}
                                        key={recruta.id || recruta._id}
                                        className="group relative p-12 rounded-[3.5rem] border shadow-2xl transition-all duration-700 hover:border-accent/40 bg-card-auto"
                                    >
                                        {/* STATUS HUD */}
                                        <div className="absolute top-8 right-10 flex items-center gap-3 px-4 py-1.5 rounded-full border border-yellow-500/20 text-yellow-500 bg-yellow-500/5">
                                            <FiClock className="animate-pulse" size={12} />
                                            <span className="text-[9px] font-black uppercase tracking-widest">{recruta.status}</span>
                                        </div>

                                        <div className="flex items-center gap-10 mb-12">
                                            <div className="w-20 h-20 rounded-[2.5rem] border border-auto flex items-center justify-center text-accent font-black text-4xl bg-main-auto shadow-inner group-hover:rotate-6 transition-transform">
                                                {recruta.name?.substring(0, 2).toUpperCase() || recruta.username?.substring(0, 2).toUpperCase()}
                                            </div>
                                            <div className="overflow-hidden space-y-2">
                                                <h3 className="font-black uppercase text-2xl md:text-3xl text-auto tracking-tighter group-hover:text-accent transition-colors truncate">
                                                    {recruta.name || 'Recruta Anônimo'}
                                                </h3>
                                                <div className="flex items-center gap-3 opacity-40 group-hover:opacity-100 transition-opacity">
                                                    <FiTerminal className="text-accent" size={14} />
                                                    <span className="text-[11px] font-bold text-auto uppercase tracking-widest">{recruta.username}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* INFO TÉCNICA CÉLULA */}
                                        <div className="grid grid-cols-2 gap-6 p-8 rounded-[2rem] border border-auto bg-main-auto mb-12 shadow-inner relative overflow-hidden">
                                            <div className="space-y-2 relative z-10">
                                                <span className="block text-[8px] font-black uppercase opacity-20 text-auto tracking-[0.2em]">Data de Ingressão</span>
                                                <p className="text-[12px] font-black text-auto">
                                                    {new Date(recruta.createdAt).toLocaleDateString()} <span className="opacity-30">//</span> {new Date(recruta.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                </p>
                                            </div>
                                            <div className="space-y-2 relative z-10">
                                                <span className="block text-[8px] font-black uppercase opacity-20 text-auto tracking-[0.2em]">Escopo Solicitado</span>
                                                <p className="text-[12px] font-black text-accent uppercase tracking-widest">Nível Operacional</p>
                                            </div>
                                            <FiLayers className="absolute -right-4 -bottom-4 text-auto opacity-[0.03]" size={80} />
                                        </div>

                                        {/* COMANDOS DE JURISDIÇÃO */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <button 
                                                onClick={() => handleApprove(recruta.id || recruta._id)}
                                                className="group/btn flex items-center justify-center gap-4 py-5 rounded-2xl font-black uppercase text-[11px] tracking-[0.3em] transition-all shadow-2xl active:scale-95 border border-transparent"
                                                style={{ backgroundColor: 'var(--accent-color)', color: '#000' }}
                                            >
                                                <FiCheckCircle size={20} strokeWidth={3} className="group-hover/btn:scale-110 transition-transform" /> 
                                                Autorizar Acesso
                                            </button>
                                            <button 
                                                onClick={() => handleReject(recruta.id || recruta._id)}
                                                className="flex items-center justify-center gap-4 py-5 rounded-2xl border border-auto text-auto opacity-30 font-black uppercase text-[11px] tracking-[0.3em] hover:opacity-100 hover:border-red-500 hover:text-red-500 transition-all bg-main-auto active:scale-95"
                                            >
                                                <FiXCircle size={20} /> Negar Ativo
                                            </button>
                                        </div>

                                        <FiAlertTriangle className="absolute -bottom-4 -left-4 text-accent opacity-0 group-hover:opacity-[0.04] pointer-events-none transition-opacity duration-1000" size={180} />
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            </div>
        </MainLayout>
    );
}